import React, { useEffect, useMemo, useState } from "react";
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from 'react-toastify';
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Badge,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";

// Import services
import {
  fetchBidangs,
  createBidangs,
  updateBidangs,
  deleteBidangs
} from '../../services/bidangService';

// Import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DeleteModal from '../../components/Common/DeleteModal';

const Bidang = () => {
  // Meta title
  document.title = "Bidang";

  // State management
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [bidang, setBidang] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });
  const [filters, setFilters] = useState({
    jenisSeleksi: ""
  });

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setBidang(null);
  };

  const handleConfirmDelete = async () => {
    try {
      if (bidang && bidang.id_bidang) {
        const response = await deleteBidangs(bidang.id_bidang);
        if (response.success) {
          toast.success("Bidang berhasil dihapus");
          fetchData(pagination.page, pagination.per_page, filters);
        } else {
          toast.error(response.message || "Gagal menghapus bidang");
        }
      }
    } catch (error) {
      console.error("Error deleting bidang:", error);
      toast.error(error.response?.data?.message || "Gagal menghapus bidang");
    } finally {
      handleCloseDeleteModal();
    }
  };

  // Fetch data function
  const fetchData = async (page = 1, per_page = 10, filterParams = {}) => {
    try {
      setLoading(true);
      const response = await fetchBidangs(page, per_page, filterParams);

      // Pastikan struktur respons sesuai dengan yang diharapkan
      setData(response.data || []);
      setPagination({
        page: response.meta?.page || 1,
        per_page: response.meta?.per_page || 10,
        total: response.meta?.total || 0,
        last_page: response.meta?.last_page || Math.ceil((response.meta?.total || 0) / (response.meta?.per_page || 10))
      });
    } catch (error) {
      console.error("Error in fetchData:", error);
      toast.error("Gagal memuat data bidang");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchData();
  }, []);

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));

    // Apply filters immediately when changed
    fetchData(1, pagination.per_page, {
      ...filters,
      [name]: value
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      jenisSeleksi: ""
    });
    fetchData();
  };

  // Form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      nama_bidang: bidang?.nama_bidang || "",
      jenis_seleksi: bidang?.jenis_seleksi || "",
      deskripsi: bidang?.deskripsi || "",
    },

    validationSchema: Yup.object({
      nama_bidang: Yup.string().required("Nama Bidang wajib diisi"),
      jenis_seleksi: Yup.string().required("Jenis Seleksi wajib diisi"),
      deskripsi: Yup.string().required("Deskripsi wajib diisi"),
    }),

    onSubmit: async (values) => {
      try {
        let response;
        if (isEdit) {
          response = await updateBidangs(bidang.id_bidang, values);
        } else {
          response = await createBidangs(values);
        }

        if (response.success) {
          toast.success(response.message || (isEdit ? "Bidang berhasil diperbarui" : "Bidang berhasil ditambahkan"));
          fetchData(pagination.page, pagination.per_page, filters);
          toggleModal();
        } else {
          if (response.code === 409) {
            toast.error(response.message || "Terjadi Kesalahan saat menyimpan");
          } else {
            toast.error(response.message || "Gagal menyimpan bidang");
          }
        }
      } catch (error) {
        console.error("Error in form submission:", error);
        toast.error(error.response?.data?.message || "Terjadi kesalahan saat menyimpan");
      }
    },
  });

  // Modal handling
  const toggleModal = () => {
    if (modal) {
      setModal(false);
      setBidang(null);
      setIsEdit(false);
      validation.resetForm();
    } else {
      setModal(true);
    }
  };

  const handleBidangClick = (bidangData) => {
    setBidang(bidangData);
    setIsEdit(true);
    toggleModal();
  };

  // Delete handling
  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = (bidangData) => {
    setBidang(bidangData);
    setDeleteModal(true);
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Nama Bidang",
        accessorKey: "nama_bidang",
        enableColumnFilter: false,
      },
      {
        header: 'Jenis Seleksi',
        accessorKey: "jenis_seleksi",
        enableColumnFilter: false,
      },
      {
        header: 'Deskripsi',
        accessorKey: "deskripsi",
        enableColumnFilter: false,
        cell: (cell) => (
          <div style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%'
          }}>
            {cell.getValue()}
          </div>
        )
      },
      {
        header: 'Created At',
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cellProps) => {
          const date = new Date(cellProps.getValue());
          return date.toISOString().split('T')[0];
        }
      },
      {
        header: 'Action',
        enableColumnFilter: false,
        cell: (cellProps) => (
          <div className="hstack gap-2">
             <Link
                    to="#"
                        className="btn btn-sm btn-soft-info"
                        onClick={() => {
                    const bidangData = cellProps.row.original;
                    handleBidangClick(bidangData);
                }}
                    id={`edittooltip-${cellProps.row.original.id_bidang}`}
                >
            <i className="mdi mdi-pencil-outline" />
            <UncontrolledTooltip placement="top" target={`edittooltip-${cellProps.row.original.id_bidang}`} >
                Edit
            </UncontrolledTooltip>
            </Link>
            <Link
                to="#"
                className="btn btn-sm btn-soft-danger"
                onClick={() => {
                    const bidangData = cellProps.row.original;
                    onClickDelete(bidangData);
                }}
                id={`deletetooltip-${cellProps.row.original.id_bidang}`}
                >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id_bidang}`}>
                    Delete
                </UncontrolledTooltip>
                </Link>
          </div>
        )
      },
    ],
    []
  );

  // Custom Pagination Component
  const CustomPagination = () => {
    const { page, last_page } = pagination;

    // Jika hanya ada 1 halaman, jangan tampilkan pagination
    if (last_page <= 1) return null;

    // Generate page numbers
    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(last_page, startPage + maxVisiblePages - 1);

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <PaginationItem key={i} active={i === page}>
            <PaginationLink onClick={() => fetchData(i, pagination.per_page, filters)}>
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }

      return pages;
    };

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Menampilkan <span className="fw-semibold">{((page - 1) * pagination.per_page) + 1}</span> sampai{' '}
          <span className="fw-semibold">{Math.min(page * pagination.per_page, pagination.total)}</span> dari{' '}
          <span className="fw-semibold">{pagination.total}</span> data
        </div>

        <div className="d-flex align-items-center">
          <Pagination className="pagination pagination-rounded mb-0">
            <PaginationItem disabled={page === 1}>
              <PaginationLink
                previous
                onClick={() => page > 1 && fetchData(page - 1, pagination.per_page, filters)}
              >
                &lt;
              </PaginationLink>
            </PaginationItem>

            {getPageNumbers()}

            <PaginationItem disabled={page === last_page}>
              <PaginationLink
                next
                onClick={() => page < last_page && fetchData(page + 1, pagination.per_page, filters)}
              >
                &gt;
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </div>
      </div>
    );
  };


  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={handleCloseDeleteModal}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bidang" breadcrumbItem="List Bidang" />
          {
            loading ? <Spinners setLoading={setLoading} />
              :
              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody className="border-bottom">
                      <div className="d-flex align-items-center justify-content-between">
                        <h5 className="mb-0 card-title flex-grow-1">List Bidang</h5>
                        <div className="d-flex align-items-center">
                          <Button color="primary" onClick={toggleModal}>
                            <i className="mdi mdi-plus me-1"></i>
                            Tambah
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                    <CardBody>
                      {/* Filter Section */}
                      <div className="mb-4">
                        <Row className="align-items-end">
                          {/* Kolom untuk Input Filter */}
                          <Col md={3} className="mb-3"> {/* Menggunakan md={3} untuk lebar lebih kecil */}
                            <Label htmlFor="jenisSeleksiFilter">Jenis Seleksi</Label>
                            <Input
                              id="jenisSeleksiFilter"
                              type="select"
                              name="jenisSeleksi"
                              value={filters.jenisSeleksi}
                              onChange={handleFilterChange}
                              className="form-select"
                            >
                              <option value="">Semua Jenis Seleksi</option>
                              <option value="CPNS">CPNS</option>
                              <option value="PPPK">PPPK</option>
                              <option value="KEDINASAN">KEDINASAN</option>
                              <option value="TNI">TNI</option>
                              <option value="POLRI">POLRI</option>
                              <option value="SPPI">SPPI</option>
                            </Input>
                          </Col>
                          {/* Kolom untuk Tombol Reset */}
                          <Col md={2} className="mb-3"> {/* Menggunakan md={2} untuk tombol, lebih lebar */}
                            {/* Label kosong agar tombol sejajar dengan input di atasnya */}
                            <Label style={{ visibility: 'hidden' }}>Reset Filter</Label>
                            <Button color="danger" outline onClick={clearFilters} className="w-100">
                              <i className="mdi mdi-refresh me-1"></i> Reset
                            </Button>
                          </Col>
                        </Row>
                      </div>

                      <TableContainer
                        columns={columns}
                        data={data}
                        isGlobalFilter={false}
                        isPagination={false}
                        customPageSize={pagination.per_page}
                        tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                      />
                      <CustomPagination />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
          }

          {/* Modal for Add/Edit */}
          <Modal isOpen={modal} toggle={toggleModal}>
            <ModalHeader toggle={toggleModal} tag="h4">
              {!!isEdit ? "Edit Bidang" : "Tambah Bidang"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={validation.handleSubmit}>
                <Row>
                  <Col md="12">
                    <div className="mb-3">
                      <Label>Nama Bidang</Label>
                      <Input
                        name="nama_bidang"
                        type="text"
                        placeholder="Nama Bidang"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.nama_bidang || ""}
                        invalid={!!(validation.touched.nama_bidang && validation.errors.nama_bidang)}
                      />
                      <FormFeedback>{validation.errors.nama_bidang}</FormFeedback>
                    </div>
                    <div className="mb-3">
                      <Label>Jenis Seleksi</Label>
                      <Input
                        name="jenis_seleksi"
                        type="select"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.jenis_seleksi || ""}
                        invalid={!!(validation.touched.jenis_seleksi && validation.errors.jenis_seleksi)}
                      >
                        <option value="">Pilih Jenis Seleksi</option>
                        <option value="CPNS">CPNS</option>
                        <option value="PPPK">PPPK</option>
                        <option value="KEDINASAN">KEDINASAN</option>
                        <option value="TNI">TNI</option>
                        <option value="POLRI">POLRI</option>
                        <option value="SPPI">SPPI</option>
                      </Input>
                      <FormFeedback>{validation.errors.jenis_seleksi}</FormFeedback>
                    </div>
                    <div className="mb-3">
                      <Label>Deskripsi</Label>
                      <Input
                        name="deskripsi"
                        type="textarea"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.deskripsi || ""}
                        invalid={!!(validation.touched.deskripsi && validation.errors.deskripsi)}
                      />
                      <FormFeedback>{validation.errors.deskripsi}</FormFeedback>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <div className="text-end">
                    <Button color="success" type="submit">
                        {isEdit ? "Update" : "Simpan"}
                    </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
}

export default Bidang;